import {type CompPassRecipient} from 'api/comp-passes/comp-pass.types';
import {type CompPassesEnabledType} from 'api/facilities/facilities.types';
import formatPhoneNumber from 'utils/format-phone-number';

export const BETA_COMP_PASS_LIMIT = 500;

export const TRANSIENT_LABEL_MAX_LENGTH = 50;

export const TRANSIENT_MAX_DURATION_HOURS = 24;

/** Max transient comp-pass duration when the facility has extended comp passes enabled (31 days). */
export const TRANSIENT_EXTENDED_MAX_DURATION_DAYS = 31;

export const TRANSIENT_TIME_STEP_MINUTES = 30;

/** Default before/after offset applied when custom event offsets are enabled (1 hour). */
export const DEFAULT_EVENT_OFFSET = '01:00';

/** Maximum allowed before/after custom event offset (24 hours). */
export const MAX_EVENT_OFFSET_HOURS = 24;

interface MaskManualInputValuesProps {
    value: string | number;
    inputType: string;
}

export const maskManualInputValues = ({
    value,
    inputType,
}: MaskManualInputValuesProps) => {
    if (typeof value !== 'string') {
        return value;
    }

    switch (inputType) {
        case 'email': {
            const maskedValue = value.toLowerCase();

            return maskedValue;
        }
        case 'tel': {
            const maskedValue = formatPhoneNumber(value);

            return maskedValue !== '' ? maskedValue : value;
        }
        case 'text': {
            const maskedValue = value.toUpperCase();

            return maskedValue !== '' ? maskedValue : value;
        }
        default: {
            return value;
        }
    }
};

interface CalculateTotalNumberOfPassesProps {
    recipients: CompPassRecipient[];
    selectionCount?: number;
}

export const calculateTotalNumberOfPasses = ({
    recipients,
    selectionCount,
}: CalculateTotalNumberOfPassesProps) => {
    return recipients.reduce((acc, current) => {
        return acc + current.quantity * (selectionCount ? selectionCount : 1);
    }, 0);
};

type PaginateArrayProps<T> = {
    data: T[];
    pageNumber: number;
    pageSize: number;
};

export const paginateArray = <T>({
    data,
    pageNumber,
    pageSize,
}: PaginateArrayProps<T>) => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = pageNumber * pageSize;

    return data.slice(startIndex, endIndex);
};

interface AnimationPropsArgs {
    show: boolean;
    index?: number;
}

export const animationProps = ({show, index = 0}: AnimationPropsArgs) => {
    const calculatedDelay = index * 0.1 + 0.125;

    return {
        in: show,
        unmountOnExit: true,
        transition: {
            enter: {
                delay: calculatedDelay,
                ease: 'easeInOut',
            },
        },
    };
};

export const INPUT_NAMES = {
    email: 'email',
    phoneNumber: 'phoneNumber',
    licensePlate: 'licensePlate',
    passesPerEvent: 'quantity',
    ccEmail: 'ccEmail',
    quantity: 'quantity',
};

export const MANUAL_INPUTS = [
    {
        isOptional: false,
        isRequired: true,
        label: 'Email*',
        name: 'email',
        type: 'email',
    },

    {
        isOptional: false,
        isRequired: true,
        label: 'Passes Per Event*',
        name: 'quantity',
        type: 'number',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'Phone Number',
        name: 'phoneNumber',
        type: 'tel',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'License Plate',
        name: 'licensePlate',
        type: 'text',
    },
    {
        isOptional: true,
        isRequired: false,
        label: 'CC Email',
        name: 'ccEmail',
        type: 'email',
    },
] as const;

export const ADD_RECIPIENTS_METHOD = {
    UPLOAD: 'upload',
    MANUAL: 'manual',
    SHARED_LINK: 'shared_link',
};

export const PASS_TYPE = {
    EVENT: 'event',
    TRANSIENT: 'transient',
} as const satisfies Record<string, CompPassesEnabledType>;

/**
 * Builds the "X Selected" counter copy shown atop the wizard steps: events for
 * the event flow, transient time-frames (singular/plural) for the transient
 * flow.
 */
export const getSelectionCountLabel = ({
    count,
    isTransientFlow,
}: {
    count: number;
    isTransientFlow: boolean;
}) =>
    isTransientFlow
        ? `${count} Transient Timeframe${count === 1 ? '' : 's'} Selected`
        : `${count} Events Selected`;

export const DEFAULT_FORM_ROW = {
    email: '',
    phoneNumber: '',
    licensePlate: '',
    quantity: 1,
    ccEmail: '',
} as const;

export const COMP_PASS_STATUSES = {
    readyToSend: {
        label: 'Ready to Send',
        id: 'readyToSend',
        description:
            'All required fields are complete. Your passes are ready to send.',
        backgroundColor: 'green.100',
        color: 'text.primary.default',
    },
};
